import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { QueryParams } from '@/types';

interface User {
  sub: string;
  username: string;
  'cognito:groups': string[];
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const membershipId = ref<string | null>(null);
  const successUrl = ref<string | null>(null);
  const cancelUrl = ref<string | null>(null);
  const productId = ref<string | null>(null);
  const user = ref<User | null>(null);

  const isAuthenticated = computed(() => !!accessToken.value);
  const hasValidToken = computed(() => {
    if (!accessToken.value) {
      console.log('🔒 hasValidToken: no access token');
      return false;
    }

    const parts = accessToken.value.split('.');
    if (parts.length !== 3) {
      console.log('🔒 hasValidToken: invalid JWT format (parts.length !== 3)');
      return false;
    }

    try {
      const payload = JSON.parse(atob(parts[1]));
      const now = Date.now() / 1000;
      const isValid = payload.exp && payload.exp > now;
      console.log('🔒 hasValidToken check:', {
        hasExp: !!payload.exp,
        exp: payload.exp,
        now: now,
        timeUntilExpiry: payload.exp ? payload.exp - now : 'N/A',
        isValid
      });
      return isValid;
    } catch (error) {
      console.log('🔒 hasValidToken: error parsing token', error);
      return false;
    }
  });

  function isValidJwtFormat(token: string): boolean {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    try {
      // Validate that each part is valid base64
      atob(parts[0]); // header
      atob(parts[1]); // payload
      return true;
    } catch {
      return false;
    }
  }

  function validateAndFormatUrl(url: string): string {
    try {
      return new URL(url.indexOf('http') === -1 ? `http://${url}` : url).toString();
    } catch {
      console.warn(`Invalid URL provided: ${url}`);
      return url; // Return original if validation fails
    }
  }

  function setQueryParams(params: QueryParams) {
    console.log('📥 setQueryParams called with:', {
      hasAccessToken: !!params.access_token,
      hasMembershipId: !!params.membership_id,
      hasProductId: !!params.product_id,
      accessTokenLength: params.access_token?.length,
    });

    if (params.access_token) {
      const isValid = isValidJwtFormat(params.access_token);
      console.log('🔍 JWT format validation:', isValid);

      if (!isValid) {
        console.warn('Invalid JWT token format provided');
        accessToken.value = null;
        user.value = null;
      } else {
        accessToken.value = params.access_token;
        console.log('✅ Access token set in store');
        try {
          const payload = JSON.parse(atob(params.access_token.split('.')[1]));
          console.log('📜 JWT payload:', {
            sub: payload.sub,
            exp: payload.exp,
            iat: payload.iat,
            hasGroups: !!payload['cognito:groups'],
          });
          user.value = {
            sub: payload.sub,
            username: payload.username,
            'cognito:groups': payload['cognito:groups'] || [],
          };
        } catch (error) {
          console.error('Failed to parse JWT token payload:', error);
          accessToken.value = null;
          user.value = null;
        }
      }
    }

    membershipId.value = params.membership_id || null;
    successUrl.value = params.success_url ? validateAndFormatUrl(params.success_url) : null;
    cancelUrl.value = params.cancel_url ? validateAndFormatUrl(params.cancel_url) : null;
    productId.value = params.product_id || null;
  }

  function clearAuth() {
    accessToken.value = null;
    membershipId.value = null;
    successUrl.value = null;
    cancelUrl.value = null;
    productId.value = null;
    user.value = null;
  }

  function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (accessToken.value) {
      headers.Authorization = `Bearer ${accessToken.value}`;
    }
    return headers;
  }

  return {
    accessToken,
    membershipId,
    successUrl,
    cancelUrl,
    productId,
    user,
    isAuthenticated,
    hasValidToken,
    setQueryParams,
    clearAuth,
    getAuthHeaders,
  };
});
